import React, { useState, useEffect } from 'react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
} from '@roger/ui-components';
import { Button } from '@roger/ui-components';
import { Input } from '@roger/ui-components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Badge } from '@roger/ui-components';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { Loader2, RefreshCw, Edit, AlertTriangle, Check } from 'lucide-react';
import {
  getAgencyInsurances,
  getAgencyClinics,
  syncClinicsFromClinicInfo,
  type AgencyInsuranceResponse,
  type AgencyClinicResponse,
} from '../services/clinicsApi';
import { updateAgencyClinicAgencyClinicsAgencyClinicIdPut } from '../services/api/sdk.gen';
import { client } from '../services/api';
import { AgencyManagementSection } from '../components/admin';

// Type for clinic edit form
type ClinicEditForm = {
  npi?: string;
  ein?: string;
  address?: string;
  city?: string;
  state?: string | null;
  zip_code?: string;
  phone?: string;
};

// US States array for the dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'AS', label: 'American Samoa' },
  { value: 'GU', label: 'Guam' },
  { value: 'MP', label: 'Northern Mariana Islands' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'VI', label: 'U.S. Virgin Islands' },
];

const Admin: React.FC = () => {
  const { user, showSuccess, showError, hasAnyPermission } = useStore(
    useShallow(state => ({
      user: state.user,
      showSuccess: state.showSuccess,
      showError: state.showError,
      hasAnyPermission: state.hasAnyPermission,
    }))
  );

  // Clinic management state
  const [isLoadingClinicInfo, setIsLoadingClinicInfo] = useState(false);

  // Clinics list state
  const [clinics, setClinics] = useState<AgencyClinicResponse[]>([]);
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingClinicId, setEditingClinicId] = useState<string | null>(null);
  const [clinicEditForm, setClinicEditForm] = useState<ClinicEditForm>({});

  // Insurance list state
  const [insurances, setInsurances] = useState<AgencyInsuranceResponse[]>([]);
  const [isLoadingInsurances, setIsLoadingInsurances] = useState(false);
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [clinicFilter, setClinicFilter] = useState<string>('all');

  // Load clinics
  const loadClinics = async () => {
    if (!user?.agency?.id) return;

    setIsLoadingClinics(true);
    try {
      const data = await getAgencyClinics(user.agency.id);
      setClinics(data);
    } catch (error) {
      console.error('Failed to load clinics:', error);
      showError('Failed to load clinic data');
    } finally {
      setIsLoadingClinics(false);
    }
  };

  // Load insurances
  const loadInsurances = async () => {
    if (!user?.agency?.id) return;

    setIsLoadingInsurances(true);
    try {
      const data = await getAgencyInsurances(user.agency.id);
      setInsurances(data);
    } catch (error) {
      console.error('Failed to load insurances:', error);
      showError('Failed to load insurance data');
    } finally {
      setIsLoadingInsurances(false);
    }
  };

  // Load clinics on mount
  useEffect(() => {
    if (user?.agency?.id) {
      loadClinics();
      loadInsurances();
    }
  }, [user?.agency?.id]);

  // Permission check - placed after all hooks to comply with Rules of Hooks
  if (!hasAnyPermission(['external-admin:access', 'emr:manage'])) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don't have permission to access Admin features. Please contact
            your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Handle get clinic info
  const handleSyncClinicInfo = async () => {
    if (!user?.agency?.system_name) {
      showError('No agency system name found');
      return;
    }

    setIsLoadingClinicInfo(true);

    try {
      // Sync clinic info to database (this now fetches from RogerDoc API internally)
      const syncResult = await syncClinicsFromClinicInfo();
      showSuccess(
        `Clinic and insurance information synced successfully. ` +
          `Clinics: ${syncResult.clinics.created_count} created, ${syncResult.clinics.updated_count} updated. ` +
          `Insurances: ${syncResult.insurances.created_count} created, ${syncResult.insurances.updated_count} updated.`
      );
      await loadClinics(); // Reload clinics after successful sync
      await loadInsurances(); // Reload insurances after successful sync
    } catch (error) {
      console.error('Failed to get clinic info:', error);

      // Scraping disabled - show error instead
      showError(
        'Failed to retrieve clinic information. Scraping is currently disabled.'
      );
    } finally {
      setIsLoadingClinicInfo(false);
    }
  };

  // Handle clinic edit
  const handleEditClinic = (clinic: AgencyClinicResponse) => {
    setEditingClinicId(clinic.id);
    setClinicEditForm({
      npi: clinic.npi || '',
      ein: clinic.ein || '',
      address: clinic.address || '',
      city: clinic.city || '',
      state: clinic.state || '',
      zip_code: clinic.zip_code || '',
      phone: clinic.phone || '',
    });
    setIsEditModalOpen(true);
  };

  // Handle clinic save
  const handleSaveClinic = async () => {
    if (!editingClinicId) return;

    try {
      await updateAgencyClinicAgencyClinicsAgencyClinicIdPut({
        client,
        path: { agency_clinic_id: editingClinicId },
        body: {
          npi: clinicEditForm.npi || undefined,
          ein: clinicEditForm.ein || undefined,
          address: clinicEditForm.address || undefined,
          city: clinicEditForm.city || undefined,
          state: clinicEditForm.state || undefined,
          zip_code: clinicEditForm.zip_code || undefined,
          phone: clinicEditForm.phone || undefined,
        },
      });
      showSuccess('Clinic updated successfully');
      setIsEditModalOpen(false);
      setEditingClinicId(null);
      setClinicEditForm({});
      await loadClinics();
    } catch (error) {
      console.error('Failed to update clinic:', error);
      showError('Failed to update clinic');
    }
  };

  // Handle clinic cancel edit
  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    setEditingClinicId(null);
    setClinicEditForm({});
  };

  // Filtered insurances
  const filteredInsurances = insurances.filter(insurance => {
    const matchesSearch =
      insuranceSearch === '' ||
      insurance.name.toLowerCase().includes(insuranceSearch.toLowerCase()) ||
      insurance.edi_payer_id
        .toLowerCase()
        .includes(insuranceSearch.toLowerCase()) ||
      insurance.clinic_name
        .toLowerCase()
        .includes(insuranceSearch.toLowerCase());

    const matchesClinic =
      clinicFilter === 'all' || insurance.clinic_name === clinicFilter;

    return matchesSearch && matchesClinic;
  });

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold'>Admin Panel</h1>
        <p className='text-muted-foreground'>
          Administrative functions and system configuration.
        </p>
      </div>

      <div className='grid gap-6'>
        {/* Agency Management Section - Read Only */}
        <AgencyManagementSection mode='readonly' />

        {/* Clinic Management Section */}
        <Card>
          <CardHeader>
            <div className='flex justify-between items-start'>
              <div>
                <CardTitle>Clinic Management</CardTitle>
              </div>
              <Button
                onClick={handleSyncClinicInfo}
                disabled={isLoadingClinicInfo}
                variant='outline'
              >
                {isLoadingClinicInfo ? (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                ) : (
                  <RefreshCw className='mr-2 h-4 w-4' />
                )}
                Sync Clinic Info
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Clinic Name</TableHead>
                      <TableHead>Clinic Key</TableHead>
                      <TableHead>NPI</TableHead>
                      <TableHead>EIN</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Clinicians</TableHead>
                      <TableHead>Case Managers</TableHead>
                      <TableHead>Branches</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoadingClinics ? (
                      <TableRow>
                        <TableCell colSpan={10} className='text-center py-8'>
                          <Loader2 className='h-6 w-6 animate-spin mx-auto' />
                          <p className='text-sm text-muted-foreground mt-2'>
                            Loading clinics...
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : clinics.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          className='text-center py-8 text-muted-foreground'
                        >
                          {user?.agency?.id
                            ? 'No clinics found. Try syncing clinic info first.'
                            : 'No agency found. Please ensure you are logged in with an agency.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      clinics.map(clinic => (
                        <TableRow key={clinic.id}>
                          <TableCell className='font-medium'>
                            {clinic.name}
                          </TableCell>
                          <TableCell className='text-sm text-muted-foreground'>
                            {clinic.clinic_key}
                          </TableCell>
                          <TableCell>
                            {clinic.npi ? (
                              <Badge variant='secondary'>{clinic.npi}</Badge>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {clinic.ein ? (
                              <Badge variant='outline'>{clinic.ein}</Badge>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          <TableCell className='text-sm'>
                            {clinic.address && (
                              <div>
                                {clinic.address}
                                {clinic.city &&
                                  clinic.state &&
                                  clinic.zip_code && (
                                    <div className='text-muted-foreground'>
                                      {clinic.city}, {clinic.state}{' '}
                                      {clinic.zip_code}
                                    </div>
                                  )}
                              </div>
                            )}
                            {!clinic.address && (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {clinic.phone ? (
                              <span className='text-sm'>{clinic.phone}</span>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant='outline'>
                              {clinic.clinicians?.length || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant='outline'>
                              {clinic.case_managers?.length || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant='outline'>
                              {clinic.branches?.length || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() => handleEditClinic(clinic)}
                            >
                              <Edit className='h-4 w-4' />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clinic Edit Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className='sm:max-w-[600px]'>
            <DialogHeader>
              <DialogTitle>Edit Clinic</DialogTitle>
              <DialogDescription>
                Update clinic information. Clinic name cannot be modified.
              </DialogDescription>
            </DialogHeader>
            <div className='grid gap-4 py-4'>
              <div className='grid gap-2'>
                <label htmlFor='npi' className='text-sm font-medium'>
                  NPI
                </label>
                <Input
                  id='npi'
                  value={clinicEditForm.npi || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      npi: e.target.value,
                    }))
                  }
                  placeholder='NPI'
                  maxLength={10}
                />
              </div>
              <div className='grid gap-2'>
                <label htmlFor='ein' className='text-sm font-medium'>
                  EIN
                </label>
                <Input
                  id='ein'
                  value={clinicEditForm.ein || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      ein: e.target.value,
                    }))
                  }
                  placeholder='EIN'
                />
              </div>
              <div className='grid gap-2'>
                <label htmlFor='phone' className='text-sm font-medium'>
                  Phone
                </label>
                <Input
                  id='phone'
                  value={clinicEditForm.phone || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                  placeholder='Phone'
                />
              </div>
              <div className='grid gap-2'>
                <label htmlFor='address' className='text-sm font-medium'>
                  Address
                </label>
                <Input
                  id='address'
                  value={clinicEditForm.address || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                  placeholder='Address'
                />
              </div>
              <div className='grid grid-cols-2 gap-2'>
                <div className='grid gap-2'>
                  <label htmlFor='city' className='text-sm font-medium'>
                    City
                  </label>
                  <Input
                    id='city'
                    value={clinicEditForm.city || ''}
                    onChange={e =>
                      setClinicEditForm(prev => ({
                        ...prev,
                        city: e.target.value,
                      }))
                    }
                    placeholder='City'
                  />
                </div>
                <div className='grid gap-2'>
                  <label htmlFor='state' className='text-sm font-medium'>
                    State
                  </label>
                  <Select
                    value={clinicEditForm.state || 'none'}
                    onValueChange={value =>
                      setClinicEditForm(prev => ({
                        ...prev,
                        state: value === 'none' ? null : value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='State' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='none'>None</SelectItem>
                      {US_STATES.map(state => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className='grid gap-2'>
                <label htmlFor='zip_code' className='text-sm font-medium'>
                  ZIP Code
                </label>
                <Input
                  id='zip_code'
                  value={clinicEditForm.zip_code || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      zip_code: e.target.value,
                    }))
                  }
                  placeholder='ZIP Code'
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button onClick={handleSaveClinic}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Insurance Providers Section */}
        <Card>
          <CardHeader>
            <CardTitle>Insurance Providers</CardTitle>
            <CardDescription>
              View insurance providers configured for your agency
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex flex-col lg:flex-row gap-4'>
              <div className='flex-1'>
                <Input
                  placeholder='Search insurances...'
                  value={insuranceSearch}
                  onChange={e => setInsuranceSearch(e.target.value)}
                  className='w-full'
                />
              </div>
              <div className='w-full lg:w-64'>
                <Select value={clinicFilter} onValueChange={setClinicFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder='Filter by clinic' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All Clinics</SelectItem>
                    {Array.from(
                      new Set(
                        insurances
                          .map(insurance => insurance.clinic_name)
                          .filter(Boolean)
                      )
                    )
                      .sort()
                      .map(clinicName => (
                        <SelectItem key={clinicName} value={clinicName}>
                          {clinicName}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={loadInsurances}
                disabled={isLoadingInsurances}
                variant='outline'
              >
                {isLoadingInsurances ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <RefreshCw className='h-4 w-4' />
                )}
              </Button>
            </div>

            <div className='rounded-md border'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Insurance Name</TableHead>
                    <TableHead>Payer ID</TableHead>
                    <TableHead>Clearinghouse</TableHead>
                    <TableHead>Clinic</TableHead>
                    <TableHead>Insurance Key</TableHead>
                    <TableHead>Payor Mapped</TableHead>
                    <TableHead>Prior Auth Required</TableHead>
                    <TableHead>Eligibility Enabled</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingInsurances ? (
                    <TableRow>
                      <TableCell colSpan={8} className='text-center py-8'>
                        <Loader2 className='h-6 w-6 animate-spin mx-auto' />
                        <p className='text-sm text-muted-foreground mt-2'>
                          Loading insurances...
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : filteredInsurances.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className='text-center py-8 text-muted-foreground'
                      >
                        {insurances.length === 0
                          ? 'No insurance providers found. Try updating from clinic info first.'
                          : 'No insurances match your search criteria.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredInsurances.map(insurance => (
                      <TableRow key={insurance.id}>
                        <TableCell className='font-medium'>
                          {insurance.name}
                        </TableCell>
                        <TableCell>
                          <Badge variant='secondary'>
                            {insurance.edi_payer_id}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant='outline'>
                            {insurance.edi_clearinghouse}
                          </Badge>
                        </TableCell>
                        <TableCell>{insurance.clinic_name}</TableCell>
                        <TableCell className='text-sm text-muted-foreground'>
                          {insurance.insurance_key}
                        </TableCell>
                        <TableCell className='text-center'>
                          {insurance.payor_id ? (
                            <Check className='h-4 w-4 text-green-600 mx-auto' />
                          ) : (
                            <div className='h-4 w-4' />
                          )}
                        </TableCell>
                        <TableCell className='text-center'>
                          {insurance.visit_authorization_required ? (
                            <Check className='h-4 w-4 text-green-600 mx-auto' />
                          ) : (
                            <div className='h-4 w-4' />
                          )}
                        </TableCell>
                        <TableCell className='text-center'>
                          {insurance.stedi_eligibility_enabled ? (
                            <Check className='h-4 w-4 text-green-600 mx-auto' />
                          ) : (
                            <div className='h-4 w-4' />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Admin;
